/**
 * A form's shell, and the date range control every report will want.
 *
 * `Form` does two things that are easy to get wrong by hand: it stops the
 * browser's own submit (which reloads the page and throws away the state), and
 * it disables the submit button while the action runs. A double-clicked Save on
 * a billing form is two invoices.
 */
import { type FormEvent, type ReactNode } from 'react';
import { Button } from './Button';
import { Field, Input } from './Field';
import { daysBetween, validDate } from '../lib/formValidation';
import { cn } from '../lib/cn';

export interface FormProps {
  readonly onSubmit: () => void;
  readonly children: ReactNode;
  /** Buttons under the fields. */
  readonly footer?: ReactNode;
  /** Disables the submit button and blocks a second press. */
  readonly busy?: boolean;
  readonly className?: string;
  readonly 'aria-label'?: string;
}

export function Form({ onSubmit, children, footer, busy = false, className, ...rest }: FormProps) {
  return (
    <form
      {...rest}
      onSubmit={(e: FormEvent) => {
        // Without this the browser navigates and every field is lost.
        e.preventDefault();
        if (busy) return;
        onSubmit();
      }}
      className={cn('flex flex-col gap-4', className)}
    >
      {children}
      {footer ? <div className="flex flex-wrap justify-end gap-2">{footer}</div> : null}
    </form>
  );
}

export type DateRangePreset = 'all' | 'today' | 'week' | 'month' | 'financial-year' | 'custom';

export interface DateRange {
  readonly from: string;
  readonly to: string;
}

export interface DateRangeSelectorProps {
  readonly value: DateRange;
  readonly onChange: (range: DateRange) => void;
  readonly presets?: readonly DateRangePreset[];
  /** ISO date for "today". Passed IN, not read from a clock. */
  readonly today: string;
  readonly fyStartMonth?: number;
  readonly className?: string;
}

const PRESET_LABELS: Record<DateRangePreset, string> = {
  all: 'All time',
  today: 'Today',
  week: 'Last 7 days',
  month: 'Last 30 days',
  'financial-year': 'This financial year',
  custom: 'Custom',
};

/**
 * Shift an ISO date by whole days, by string arithmetic.
 *
 * No `Date`. `new Date('2026-04-01')` is 31 March in some time zones, which
 * would quietly move a report's boundary by a day — and a report that disagrees
 * with itself depending on where the user is, is not a report.
 */
export function shiftIsoDate(iso: string, days: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const [, y, m, d] = match;
  const asUtc = Date.UTC(Number(y), Number(m) - 1, Number(d)) + days * 86_400_000;
  const moved = new Date(asUtc);
  const mm = String(moved.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(moved.getUTCDate()).padStart(2, '0');
  return `${moved.getUTCFullYear()}-${mm}-${dd}`;
}

/** The first day of the financial year containing `today`. */
export function financialYearStart(today: string, fyStartMonth = 4): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  if (!match) return today;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const startYear = month >= fyStartMonth ? year : year - 1;
  return `${startYear}-${String(fyStartMonth).padStart(2, '0')}-01`;
}

/** Why this range is unusable, or null when it is fine. */
export function dateRangeProblem(range: DateRange): string | null {
  if (range.from && !validDate(range.from, 'From').ok) return 'The start date is not a valid date.';
  if (range.to && !validDate(range.to, 'To').ok) return 'The end date is not a valid date.';
  if (range.from && range.to) {
    const span = daysBetween(range.from, range.to);
    if (span === null) return 'The dates could not be read.';
    if (span < 0) return 'The start date must not be after the end date.';
  }
  return null;
}

export function DateRangeSelector({
  value,
  onChange,
  presets = ['today', 'week', 'month', 'financial-year', 'all', 'custom'],
  today,
  fyStartMonth = 4,
  className,
}: DateRangeSelectorProps) {
  const problem = dateRangeProblem(value);

  function applyPreset(preset: DateRangePreset) {
    switch (preset) {
      case 'today':
        onChange({ from: today, to: today });
        return;
      case 'week':
        onChange({ from: shiftIsoDate(today, -6), to: today });
        return;
      case 'month':
        onChange({ from: shiftIsoDate(today, -29), to: today });
        return;
      case 'financial-year':
        onChange({ from: financialYearStart(today, fyStartMonth), to: today });
        return;
      case 'all':
        onChange({ from: '', to: '' });
        return;
      default:
        return;
    }
  }

  return (
    <div className={cn('flex flex-wrap items-end gap-2', className)} data-testid="date-range">
      <div role="group" aria-label="Date range presets" className="flex flex-wrap gap-1">
        {presets.map((preset) => (
          <Button
            key={preset}
            size="sm"
            variant="secondary"
            onClick={() => applyPreset(preset)}
            disabled={preset === 'custom'}
          >
            {PRESET_LABELS[preset]}
          </Button>
        ))}
      </div>

      <Field label="From" className="min-w-36" error={problem ?? undefined}>
        {(wiring) => (
          <Input
            {...wiring}
            type="date"
            value={value.from}
            onChange={(e) => onChange({ ...value, from: e.target.value })}
          />
        )}
      </Field>

      <Field label="To" className="min-w-36" error={problem ?? undefined}>
        {(wiring) => (
          <Input
            {...wiring}
            type="date"
            value={value.to}
            onChange={(e) => onChange({ ...value, to: e.target.value })}
          />
        )}
      </Field>
    </div>
  );
}
